import { useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import type {
  InfographicConfig,
  KpiLayoutData,
  TimelineLayoutData,
  ComparisonLayoutData,
  KpiItem,
} from '@mem-ai-hub/shared';

// ---------------------------------------------------------------------------
// Parse helper
// ---------------------------------------------------------------------------

export function tryParseInfographic(text: string): InfographicConfig | null {
  const trimmed = text.trim();
  if (!trimmed.startsWith('{')) return null;

  try {
    const obj = JSON.parse(trimmed);
    return validateInfographic(obj);
  } catch {
    // If it looks complete but failed, try extracting outermost JSON object
    if (trimmed.endsWith('}')) {
      try {
        const lastBrace = trimmed.lastIndexOf('}');
        const obj = JSON.parse(trimmed.slice(0, lastBrace + 1));
        return validateInfographic(obj);
      } catch {
        // genuinely invalid JSON
      }
    }
  }
  return null;
}

function validateInfographic(obj: unknown): InfographicConfig | null {
  if (!obj || typeof obj !== 'object') return null;
  const o = obj as Record<string, unknown>;
  if (o.type !== 'infographic') return null;
  if (typeof o.title !== 'string' || typeof o.fileName !== 'string') return null;
  if (!o.data || typeof o.data !== 'object') return null;

  const data = o.data as Record<string, unknown>;
  switch (data.layout) {
    case 'kpi':
      if (!Array.isArray(data.items) || data.items.length === 0) return null;
      break;
    case 'timeline':
      if (!Array.isArray(data.steps) || data.steps.length === 0) return null;
      break;
    case 'comparison':
      if (!Array.isArray(data.columns) || data.columns.length < 2) return null;
      if (!Array.isArray(data.rows) || data.rows.length === 0) return null;
      break;
    default:
      return null;
  }

  return obj as InfographicConfig;
}

// ---------------------------------------------------------------------------
// Color schemes
// ---------------------------------------------------------------------------

interface ColorPalette {
  primary: string;
  secondary: string;
  accent: string;
  bg: string;
  card: string;
  text: string;
  muted: string;
}

const COLOR_SCHEMES: Record<string, ColorPalette> = {
  blue: {
    primary: '#03A9F4',
    secondary: '#0288D1',
    accent: '#E05726',
    bg: '#F8FBFF',
    card: '#FFFFFF',
    text: '#1A2332',
    muted: '#6B7B8D',
  },
  warm: {
    primary: '#E05726',
    secondary: '#C44A20',
    accent: '#03A9F4',
    bg: '#FFFAF7',
    card: '#FFFFFF',
    text: '#2D1B0E',
    muted: '#8D7B6B',
  },
  cool: {
    primary: '#6366F1',
    secondary: '#4F46E5',
    accent: '#06B6D4',
    bg: '#F8F8FF',
    card: '#FFFFFF',
    text: '#1E1B3A',
    muted: '#7B7B8D',
  },
  neutral: {
    primary: '#475569',
    secondary: '#334155',
    accent: '#03A9F4',
    bg: '#F9FAFB',
    card: '#FFFFFF',
    text: '#1E293B',
    muted: '#94A3B8',
  },
};

function getPalette(scheme?: string): ColorPalette {
  return COLOR_SCHEMES[scheme ?? 'blue'] ?? COLOR_SCHEMES.blue;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface InfographicBlockProps {
  config: InfographicConfig;
}

export default function InfographicBlock({ config }: InfographicBlockProps) {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const palette = getPalette(config.colorScheme);

  const layoutLabel =
    config.data.layout === 'kpi'
      ? 'KPI Dashboard'
      : config.data.layout === 'timeline'
        ? 'Timeline'
        : 'Comparison';

  const handleDownload = async () => {
    if (!canvasRef.current) return;
    setDownloading(true);
    setError(null);
    try {
      const dataUrl = await toPng(canvasRef.current, {
        pixelRatio: 2,
        cacheBust: true,
        filter: (node: HTMLElement) =>
          node.getAttribute?.('data-exclude-export') !== 'true',
      });
      const link = document.createElement('a');
      link.download = `${config.fileName || 'infographic'}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="my-3">
      {/* Header card — matches SpreadsheetBlock / DocumentBlock */}
      <div className="rounded-lg border border-border-light bg-bg-secondary p-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#03A9F4]/10">
            <svg className="h-5 w-5 text-[#03A9F4]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5" />
            </svg>
          </div>

          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-text-primary font-heading">
              {config.title}
            </p>
            <p className="text-xs text-text-secondary">
              {layoutLabel} infographic
            </p>
          </div>

          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#03A9F4] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#0288D1] disabled:opacity-50"
          >
            {downloading ? (
              <>
                <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Exporting...
              </>
            ) : (
              <>
                <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Download .png
              </>
            )}
          </button>
        </div>

        {error && (
          <p className="mt-2 text-xs text-red-500">{error}</p>
        )}
      </div>

      {/* Infographic canvas — inline styles for html-to-image reliability */}
      <div
        ref={canvasRef}
        style={{
          width: 800,
          minHeight: 450,
          background: palette.bg,
          borderRadius: 12,
          padding: 40,
          fontFamily: "'Open Sans', 'Segoe UI', Helvetica, Arial, sans-serif",
          color: palette.text,
          marginTop: 8,
          overflow: 'hidden',
        }}
      >
        {/* Title */}
        <div style={{ marginBottom: 8 }}>
          <h2
            style={{
              fontFamily: "'Raleway', 'Segoe UI', Helvetica, Arial, sans-serif",
              fontSize: 26,
              fontWeight: 700,
              color: palette.text,
              margin: 0,
              lineHeight: 1.3,
            }}
          >
            {config.title}
          </h2>
          {config.data.subtitle && (
            <p
              style={{
                fontSize: 14,
                color: palette.muted,
                margin: '6px 0 0 0',
                lineHeight: 1.4,
              }}
            >
              {config.data.subtitle}
            </p>
          )}
        </div>

        {/* Divider */}
        <div
          style={{
            height: 3,
            background: `linear-gradient(to right, ${palette.primary}, ${palette.secondary})`,
            borderRadius: 2,
            margin: '16px 0 24px 0',
          }}
        />

        {/* Layout-specific renderer */}
        {config.data.layout === 'kpi' && (
          <KpiLayout data={config.data} palette={palette} />
        )}
        {config.data.layout === 'timeline' && (
          <TimelineLayout data={config.data} palette={palette} />
        )}
        {config.data.layout === 'comparison' && (
          <ComparisonLayout data={config.data} palette={palette} />
        )}

        {/* Footer branding */}
        <div
          style={{
            marginTop: 32,
            paddingTop: 16,
            borderTop: `1px solid ${palette.muted}33`,
            textAlign: 'center',
            fontSize: 11,
            color: palette.muted,
          }}
        >
          Memphis International Airport (MEM) &mdash; AI Hub
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI Layout
// ---------------------------------------------------------------------------

function KpiLayout({ data, palette }: { data: KpiLayoutData; palette: ColorPalette }) {
  const count = data.items.length;
  const columns = count <= 4 ? count : Math.ceil(count / 2);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: 16,
      }}
    >
      {data.items.map((item, i) => (
        <KpiCard key={i} item={item} palette={palette} />
      ))}
    </div>
  );
}

function KpiCard({ item, palette }: { item: KpiItem; palette: ColorPalette }) {
  const trendColor =
    item.trend === 'up' ? '#16A34A' : item.trend === 'down' ? '#DC2626' : palette.muted;
  const trendArrow =
    item.trend === 'up' ? '\u25B2' : item.trend === 'down' ? '\u25BC' : '\u2014';

  return (
    <div
      style={{
        background: palette.card,
        borderRadius: 8,
        padding: '20px 16px',
        textAlign: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        border: `1px solid ${palette.muted}22`,
      }}
    >
      {item.icon && (
        <div style={{ fontSize: 24, marginBottom: 8 }}>{item.icon}</div>
      )}
      <div
        style={{
          fontFamily: "'Raleway', 'Segoe UI', Helvetica, Arial, sans-serif",
          fontSize: 28,
          fontWeight: 700,
          color: palette.primary,
          lineHeight: 1.2,
        }}
      >
        {item.value}
      </div>
      <div
        style={{
          fontSize: 12,
          color: palette.muted,
          marginTop: 4,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        {item.label}
      </div>
      {item.change && (
        <div
          style={{
            marginTop: 8,
            fontSize: 13,
            fontWeight: 600,
            color: trendColor,
          }}
        >
          <span style={{ marginRight: 4 }}>{trendArrow}</span>
          {item.change}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Timeline Layout
// ---------------------------------------------------------------------------

function TimelineLayout({ data, palette }: { data: TimelineLayoutData; palette: ColorPalette }) {
  return (
    <div style={{ position: 'relative', paddingLeft: 48 }}>
      {data.steps.map((step, i) => {
        const isLast = i === data.steps.length - 1;
        return (
          <div key={i} style={{ position: 'relative', paddingBottom: isLast ? 0 : 24 }}>
            {/* Connector line */}
            {!isLast && (
              <div
                style={{
                  position: 'absolute',
                  left: -30,
                  top: 36,
                  bottom: 0,
                  width: 2,
                  background: `${palette.primary}44`,
                }}
              />
            )}

            {/* Circle */}
            <div
              style={{
                position: 'absolute',
                left: -42,
                top: 4,
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: palette.primary,
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: step.icon ? 14 : 13,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {step.icon || i + 1}
            </div>

            {/* Card */}
            <div
              style={{
                background: palette.card,
                borderRadius: 8,
                padding: '14px 18px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                border: `1px solid ${palette.muted}22`,
              }}
            >
              <div
                style={{
                  fontFamily: "'Raleway', 'Segoe UI', Helvetica, Arial, sans-serif",
                  fontSize: 15,
                  fontWeight: 700,
                  color: palette.text,
                  marginBottom: step.description ? 4 : 0,
                }}
              >
                {step.label}
              </div>
              {step.description && (
                <div style={{ fontSize: 13, color: palette.muted, lineHeight: 1.5 }}>
                  {step.description}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Comparison Layout
// ---------------------------------------------------------------------------

function ComparisonLayout({ data, palette }: { data: ComparisonLayoutData; palette: ColorPalette }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 13,
          lineHeight: 1.5,
        }}
      >
        <thead>
          <tr>
            {/* Empty top-left cell for row labels */}
            <th
              style={{
                padding: '10px 14px',
                textAlign: 'left',
                borderBottom: `2px solid ${palette.primary}`,
                background: palette.bg,
              }}
            />
            {data.columns.map((col, i) => (
              <th
                key={i}
                style={{
                  padding: '10px 14px',
                  textAlign: 'center',
                  fontFamily: "'Raleway', 'Segoe UI', Helvetica, Arial, sans-serif",
                  fontWeight: 700,
                  fontSize: 13,
                  color: col.highlight ? '#FFFFFF' : palette.text,
                  background: col.highlight ? palette.primary : palette.bg,
                  borderBottom: `2px solid ${palette.primary}`,
                  borderRadius: col.highlight ? '6px 6px 0 0' : undefined,
                }}
              >
                {col.icon && <span style={{ marginRight: 6 }}>{col.icon}</span>}
                {col.heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, ri) => (
            <tr key={ri}>
              <td
                style={{
                  padding: '10px 14px',
                  fontWeight: 600,
                  color: palette.text,
                  background: ri % 2 === 0 ? palette.card : `${palette.muted}0D`,
                  borderBottom: `1px solid ${palette.muted}22`,
                }}
              >
                {row.label}
              </td>
              {row.values.map((val, vi) => {
                const col = data.columns[vi];
                return (
                  <td
                    key={vi}
                    style={{
                      padding: '10px 14px',
                      textAlign: 'center',
                      color: palette.text,
                      background: col?.highlight
                        ? `${palette.primary}0D`
                        : ri % 2 === 0
                          ? palette.card
                          : `${palette.muted}0D`,
                      borderBottom: `1px solid ${palette.muted}22`,
                    }}
                  >
                    {val}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
